import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { AlertTriangle, Globe } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { useResolvedEnvironment } from '@/lib/appEnvironment';

/**
 * ProductionActionGuard — a deliberate "You are acting on PRODUCTION"
 * confirmation for the handful of irreversible / high-impact admin actions
 * (Emergency Stop, Pause/Resume Scheduler, Force M-Level, future Run-Now and
 * bulk actions).
 *
 * Behaviour:
 *   • Fires ONLY when the resolved environment is PRODUCTION (same source of
 *     truth as the EnvironmentRibbon).
 *   • In Preview / Staging / Development it is a no-op — the action proceeds
 *     immediately with NO extra prompt (a slip there is harmless test data).
 *
 * Usage:
 *   const guard = useProductionGuard();
 *   if (!(await guard({ title, description }))) return;  // user cancelled
 *   ...perform action...
 */
const ProductionGuardContext = createContext(null);

export function ProductionGuardProvider({ children }) {
  const env = useResolvedEnvironment();
  const isProd = env.key === 'production';
  const [pending, setPending] = useState(null); // { title, description, confirmLabel }
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    if (!isProd) return Promise.resolve(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setPending({
        title: opts.title || 'Run this action',
        description: opts.description || '',
        confirmLabel: opts.confirmLabel || 'Confirm on PRODUCTION',
      });
    });
  }, [isProd]);

  const settle = (value) => {
    const r = resolverRef.current;
    resolverRef.current = null;
    setPending(null);
    if (r) r(value);
  };

  return (
    <ProductionGuardContext.Provider value={confirm}>
      {children}
      <AlertDialog open={!!pending} onOpenChange={(open) => { if (!open) settle(false); }}>
        <AlertDialogContent className="border-2 border-red-300" data-testid="production-guard-dialog">
          <AlertDialogHeader>
            <div
              className="inline-flex items-center gap-2 self-start rounded-full bg-red-600 px-3 py-1 text-xs font-black uppercase tracking-wider text-white"
              data-testid="production-guard-banner"
            >
              <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.6} />
              You are acting on PRODUCTION
            </div>
            <AlertDialogTitle className="pt-2" data-testid="production-guard-title">
              {pending?.title}
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="production-guard-description">
              {pending?.description}
              <span className="mt-2 flex items-start gap-1.5 text-red-700 font-semibold">
                <Globe className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                This affects live customers and real report delivery. Continue only if you mean to.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <button
              type="button"
              onClick={() => settle(false)}
              data-testid="production-guard-cancel"
              className="mt-2 sm:mt-0 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => settle(true)}
              data-testid="production-guard-confirm"
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700 transition-colors"
            >
              {pending?.confirmLabel}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ProductionGuardContext.Provider>
  );
}

// Returns confirm(opts) → Promise<boolean>. Falls back to an allow-through
// no-op if the provider is somehow absent, so actions are never hard-blocked.
export function useProductionGuard() {
  const ctx = useContext(ProductionGuardContext);
  return ctx || (() => Promise.resolve(true));
}
